// src/components/providers/AudioProvider.tsx
import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react'
import { audioApi } from '@/lib/api'
import type { CachedAudioBriefing } from '@/lib/api'

interface AudioContextValue {
  // State
  activeBriefing: CachedAudioBriefing | null
  isPlaying: boolean
  currentTime: number
  duration: number
  volume: number
  isMuted: boolean
  playbackSpeed: number
  isLoading: boolean

  // Actions
  play: (briefing: CachedAudioBriefing) => void
  togglePlay: () => void
  seek: (time: number) => void
  setVolume: (v: number) => void
  toggleMute: () => void
  setSpeed: (s: number) => void
  stop: () => void
}

const AudioContext = createContext<AudioContextValue | null>(null)

export function useAudio() {
  const ctx = useContext(AudioContext)
  if (!ctx) throw new Error('useAudio must be used within AudioProvider')
  return ctx
}

export function AudioProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null)

  const [activeBriefing, setActiveBriefing] = useState<CachedAudioBriefing | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolumeState] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [playbackSpeed, setPlaybackSpeedState] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const play = useCallback((briefing: CachedAudioBriefing) => {
    if (!audioRef.current) return
    const isSame = activeBriefing?.id === briefing.id
    if (isSame) {
      if (isPlaying) { audioRef.current.pause() }
      else { audioRef.current.play().catch(console.error) }
      return
    }
    setActiveBriefing(briefing)
    setCurrentTime(0)
    setDuration(briefing.duration_seconds || 0)
    setIsLoading(true)
    audioRef.current.src = audioApi.getStreamUrl(briefing.id)
    audioRef.current.load()
    audioRef.current.play()
      .then(() => setIsLoading(false))
      .catch(err => { console.error('Play failed:', err); setIsLoading(false) })
  }, [activeBriefing, isPlaying])

  const togglePlay = useCallback(() => {
    if (!audioRef.current || !activeBriefing) return
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play().catch(console.error)
    }
  }, [isPlaying, activeBriefing])

  const seek = useCallback((time: number) => {
    if (!audioRef.current) return
    audioRef.current.currentTime = time
    setCurrentTime(time)
  }, [])

  const setVolume = useCallback((v: number) => {
    if (!audioRef.current) return
    audioRef.current.volume = v
    setVolumeState(v)
    setIsMuted(v === 0)
  }, [])

  const toggleMute = useCallback(() => {
    if (!audioRef.current) return
    const next = !isMuted
    audioRef.current.muted = next
    setIsMuted(next)
  }, [isMuted])

  const setSpeed = useCallback((s: number) => {
    if (!audioRef.current) return
    audioRef.current.playbackRate = s
    setPlaybackSpeedState(s)
  }, [])

  const stop = useCallback(() => {
    if (!audioRef.current) return
    audioRef.current.pause()
    audioRef.current.currentTime = 0
    setActiveBriefing(null)
    setIsPlaying(false)
    setCurrentTime(0)
  }, [])

  return (
    <AudioContext.Provider value={{
      activeBriefing, isPlaying, currentTime, duration,
      volume, isMuted, playbackSpeed, isLoading,
      play, togglePlay, seek, setVolume, toggleMute, setSpeed, stop,
    }}>
      {/* Single audio element — never unmounts */}
      <audio
        ref={audioRef}
        onTimeUpdate={() => audioRef.current && setCurrentTime(audioRef.current.currentTime)}
        onLoadedMetadata={() => { if (audioRef.current) { setDuration(audioRef.current.duration); audioRef.current.playbackRate = playbackSpeed } }}
        onEnded={() => { setIsPlaying(false); setCurrentTime(0) }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
      />
      {children}
    </AudioContext.Provider>
  )
}